export interface CycleAnalysisParams {
  sensor: any
  start_date: Date
  end_date: Date
  window_period?: string
  min_cycle_duration?: number
  max_cycle_duration?: number
  change_percentile?: number
  min_data_points?: number
  smoothing_factor?: number
  use_neighbor_average?: boolean
  rate_multiplier?: number
}

export interface Reading {
  t: string
  v: number
}

export interface Change {
  time: Date
  rate: number
  value: number
  original_value: number
}

export interface Edge {
  time: Date
  type: 'rising' | 'falling'
  rate: number
  value: number
}

export interface CyclePeriod {
  start: string
  end: string
  duration: number
}

export interface CycleAnalysisResult {
  cycle_count: number
  total_time: number
  average_time: number
  periods: CyclePeriod[]
  start_date: Date
  end_date: Date
}

export class CycleAnalysisService {
  private sensor: any
  private start_date: Date
  private end_date: Date
  private window_period: string
  private min_cycle_duration: number
  private max_cycle_duration: number
  private change_percentile: number
  private min_data_points: number
  private smoothing_factor: number
  private use_neighbor_average: boolean
  private rate_multiplier: number

  private readings: Reading[] = []
  private periods: CyclePeriod[] = []

  constructor(params: CycleAnalysisParams) {
    this.sensor = params.sensor
    this.start_date = params.start_date
    this.end_date = params.end_date || new Date()
    this.window_period = params.window_period || '5s'
    this.min_cycle_duration = params.min_cycle_duration || 20
    this.max_cycle_duration = params.max_cycle_duration || 350
    this.change_percentile = params.change_percentile || 0
    this.min_data_points = params.min_data_points || 10
    this.smoothing_factor = params.smoothing_factor || 1.0
    this.use_neighbor_average = params.use_neighbor_average || true
    this.rate_multiplier = params.rate_multiplier || 4.0

    if (!(this.start_date instanceof Date)) {
      this.start_date = new Date(this.start_date)
    }
    if (!(this.end_date instanceof Date)) {
      this.end_date = new Date(this.end_date)
    }
  }

  async call(): Promise<CycleAnalysisResult> {
    await this.fetchReadings()
    this.detectMixingCycles()
    this.calculateCycleMetrics()
    return this.formatResult()
  }

  private async fetchReadings(): Promise<void> {
    if (this.sensor.readings) {
      const average =
        this.sensor.readings['average_current'] ?? this.sensor.readings['AVERAGE']

      if (average) {
        this.readings = average.map((reading: any) => ({
          t: reading.t,
          v: reading.v,
        }))
      }
    }
  }

  private detectMixingCycles(): void {
    if (this.readings.length < this.min_data_points) {
      return
    }

    const smoothedReadings = this.use_neighbor_average ? this.applyNeighborAverage(this.readings) : this.readings

    const changes = this.calculateRateChanges(smoothedReadings)

    const threshold = this.calculateDynamicThreshold(changes)

    const edges = this.detectEdges(changes, threshold)

    this.periods = this.formCyclesFromEdges(edges)
  }

  private applyNeighborAverage(readings: Reading[]): Reading[] {
    if (readings.length < 3) return readings

    const smoothed: Reading[] = []

    readings.forEach((reading, index) => {
      let avgValue: number

      if (index === 0) {
        avgValue = (readings[0].v + readings[1].v) / 2.0
      } else if (index === readings.length - 1) {
        avgValue = (readings[index - 1].v + readings[index].v) / 2.0
      } else {
        avgValue = (readings[index - 1].v + readings[index].v + readings[index + 1].v) / 3.0
      }

      smoothed.push({
        t: reading.t,
        v: avgValue,
      })
    })

    return smoothed
  }

  private calculateRateChanges(readings: Reading[]): Change[] {
    const changes: Change[] = []

    for (let i = 0; i < readings.length - 1; i++) {
      const current = readings[i]
      const nextPoint = readings[i + 1]

      const timeDiff = new Date(nextPoint.t).getTime() - new Date(current.t).getTime()
      const valueDiff = nextPoint.v - current.v

      const rate = timeDiff > 0 ? valueDiff / (timeDiff / 1000) : 0 // Convertendo para segundos

      changes.push({
        time: new Date(nextPoint.t),
        rate,
        value: nextPoint.v,
        original_value: current.v,
      })
    }

    return changes
  }

  private calculateDynamicThreshold(changes: Change[]): number {
    const rates = changes.map((c) => Math.abs(c.rate)).filter((r) => r > 0)

    if (rates.length === 0) {
      return 1.0
    }

    const avgRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length
    const threshold = avgRate * this.rate_multiplier

    if (this.change_percentile > 0) {
      const sortedRates = rates.sort((a, b) => a - b)
      const percentileIndex = Math.floor((sortedRates.length * this.change_percentile) / 100.0)
      const percentileThreshold = sortedRates[percentileIndex] || avgRate

      const rawThreshold = percentileThreshold * this.smoothing_factor
      const adjustedThreshold = Math.max(rawThreshold, threshold * 0.75) //0.75 = eleva 25% a mais o Threshold fazendo não ficar baixo o suficiente
      //  para não detectar os ciclos e não tão alto e detectar falsos ciclos, valor pode ser modificado se gerar falsos ciclos

      return adjustedThreshold
    } else {
      const result = threshold * this.smoothing_factor
      return result
    }
  }

  private detectEdges(changes: Change[], threshold: number): Edge[] {
    const edges: Edge[] = []

    changes.forEach((change, index) => {
      if (change.rate > threshold) {
        edges.push({
          time: change.time,
          type: 'rising',
          rate: change.rate,
          value: change.value,
        })
      } else if (change.rate < -threshold) {
        edges.push({
          time: change.time,
          type: 'falling',
          rate: change.rate,
          value: change.value,
        })
      }
    })

    return edges
  }

  private formCyclesFromEdges(edges: Edge[]): CyclePeriod[] {
    const periods: CyclePeriod[] = []
    let cycleStart: Edge | null = null

    edges.forEach((edge, index) => {
      switch (edge.type) {
        case 'rising':
          cycleStart = edge
          break
        case 'falling':
          if (cycleStart) {
            const durationSeconds = (edge.time.getTime() - cycleStart.time.getTime()) / 1000

            if (durationSeconds >= this.min_cycle_duration && durationSeconds <= this.max_cycle_duration) {
              const period = {
                start: cycleStart.time.toISOString(),
                end: edge.time.toISOString(),
                duration: Math.round((durationSeconds / 60.0) * 100) / 100,
              }
              periods.push(period)
            }

            cycleStart = null
          }
          break
      }
    })

    if (cycleStart) {
      const cycleEndTime = this.end_date || new Date()

      // Garantir que cycleEndTime é um objeto Date válido
      const validEndTime = cycleEndTime instanceof Date ? cycleEndTime : new Date(cycleEndTime)

      const durationSeconds = (validEndTime.getTime() - cycleStart.time.getTime()) / 1000

      if (durationSeconds >= this.min_cycle_duration && durationSeconds <= this.max_cycle_duration) {
        const period = {
          start: cycleStart.time.toISOString(),
          end: validEndTime.toISOString(),
          duration: Math.round((durationSeconds / 60.0) * 100) / 100,
        }
        periods.push(period)
      }
    }

    return periods
  }

  private calculateCycleMetrics(): void {
    // Métricas já calculadas nos períodos
  }

  private formatResult(): CycleAnalysisResult {
    const cycleCount = this.periods.length
    const durations = this.periods.map((p) => p.duration)
    const totalTime = durations.reduce((sum, duration) => sum + duration, 0)
    const averageTime = durations.length > 0 ? Math.round((totalTime / cycleCount) * 100) / 100 : 0.0

    return {
      cycle_count: cycleCount,
      total_time: Math.round(totalTime * 100) / 100,
      average_time: Math.round(averageTime * 100) / 100,
      periods: this.periods,
      start_date: this.start_date,
      end_date: this.end_date,
    }
  }
}
